import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import SearchInput from 'assets/components/inputs/search/SearchInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import { compact, filter, flatten, sortBy, uniq, uniqBy } from 'lodash';
import Permission from 'models/core/permission/Model.Permission';
import Role from 'models/core/role/Model.Role';
import { useMemo, useState } from 'react';

type UserEditRolesProps = {
  roles: Role[];
  permissions: Permission[];
  isLoading: boolean;
};
export default function UserEditRoles({ roles, permissions, isLoading }: UserEditRolesProps) {
  const { lang } = getLocales();
  const { form } = useModalForm<Api.Core.User.Save.IRequest>();

  const [showSelectedPermissionsOnly, setShowSelectedPermissionsOnly] = useState(false);
  const [appFilter, setAppFilter] = useState<number>();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRoles = useMemo(() => {
    function validateQuery(it: Role) {
      return new RegExp(searchQuery.toLowerCase()).test((it.name + ' ' + it.description + ' ').toLowerCase());
    }
    return sortBy(
      filter(
        compact(roles),
        (it) =>
          (!searchQuery ? true : validateQuery(it)) &&
          (!appFilter ? true : it.permissions.some((p) => p.application.id === appFilter))
      ),
      (it) => uniq(flatten(sortBy(it.permissions.map((p) => p.application.name)))).join(', '),
      (it) => it.name
    );
  }, [permissions, searchQuery, appFilter]);

  const filterApps = useMemo(
    () =>
      sortBy(
        uniqBy(compact(flatten(permissions?.map((it) => it.application))), (it) => it.id),
        (it) => it.name
      ),
    [permissions]
  );
  const roleList = showSelectedPermissionsOnly
    ? filteredRoles?.filter((r) => form.data.roles?.find((it) => it.id === r.id))
    : filteredRoles;
  const areAllSelected = form.data.roles?.length === roleList.length;
  const areNoneSelected = form.data.roles?.length === 0;

  return (
    <table className={ThemeTableSimple}>
      <thead>
        <tr>
          <th>
            <Button
              class={{ button: ThemeButtonCircle }}
              media="fas-tasks"
              onClick={() => setShowSelectedPermissionsOnly((it) => !it)}
              isLoading={isLoading}
              active={showSelectedPermissionsOnly}
              htmlElementProps={{ title: lang.showOnlySelected }}
            />
          </th>
          <th colSpan={1}>
            <SearchInput onSearch={(q) => setSearchQuery(q)} />
          </th>
          <th>
            <SelectInput
              name="filter"
              placeholder={lang.application}
              data={filterApps.map((item) => item.displayInfo)}
              value={appFilter}
              disabled={!filterApps.length}
              onChange={({ filter }) => setAppFilter(filter)}
              style={{ marginTop: 0 }}
              allowClear
            />
          </th>
        </tr>
        <tr>
          <th>
            <Button
              class={{ button: ThemeButtonCircle }}
              media={areAllSelected ? 'fas-check-square' : areNoneSelected ? 'far-square' : 'fas-minus-square'}
              disabled={!permissions.length}
              onClick={async () => {
                form.update({ roles: areAllSelected ? [] : compact(roleList).map((role) => ({ id: role.id })) });
              }}
              isLoading={isLoading}
              active={!areNoneSelected}
              htmlElementProps={{ title: lang.selectAll }}
            />
          </th>
          <th>{lang.role}</th>
          <th>{lang.application}</th>
        </tr>
      </thead>
      {roleList?.length > 0 && (
        <tbody>
          <DataMap
            data={roleList}
            render={({ data: role }) => {
              const currentRole = form.data.roles?.find((it) => it.id === role.id);
              const permissionNames = sortBy(role.permissions.map((it) => it.name)).join(', ');
              return (
                <tr key={role.id}>
                  <td>
                    <Button
                      class={{ button: ThemeButtonCircle }}
                      htmlElementProps={{
                        title: currentRole ? lang.unselect : lang.select,
                      }}
                      onClick={() => {
                        if (currentRole)
                          form.update({ roles: form.data.roles?.filter((it) => it.id !== currentRole?.id) });
                        else form.update({ roles: [...(form.data.roles || []), { id: role.id }] });
                      }}
                      media={currentRole ? 'fas-check-square' : 'far-square'}
                    />
                  </td>
                  <td>
                    <b>{role.name}</b>
                    <br />
                    <span>{permissionNames}</span>
                  </td>
                  <td>{uniq(flatten(sortBy(role.permissions.map((it) => it.application.name)))).join(', ')}</td>
                </tr>
              );
            }}
          />
        </tbody>
      )}
    </table>
  );
}
